import React from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { PeriodTransaction, KPIData, DashboardProduct, StoreSelectOption } from './types';
import { formatCurrency, getGroupKey, getTimeLabels } from './FinancialDashboardController';
import { aggregateProductData } from './Products/ProductsController';

interface Props {
  filteredData: PeriodTransaction[];
  kpiData: KPIData;
  products: DashboardProduct[];
  fromDate: Date | null;
  toDate: Date | null;
  selectedStores: StoreSelectOption[];
  monthNames: string[];
  showCash: boolean;
}

const ExportOverviewButton: React.FC<Props> = ({
  filteredData, kpiData, products, fromDate, toDate, selectedStores, monthNames, showCash,
}) => {
  const { t } = useTranslation();

  const handleExport = () => {
    if (filteredData.length === 0) return;

    const escapeCSVValue = (value: any): string => {
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const row = (...cols: any[]) => cols.map(escapeCSVValue).join(',');

    // Metadata
    const rangeLabel = (fromDate || toDate)
      ? `${fromDate ? format(fromDate, 'MM/yyyy') : '...'} – ${toDate ? format(toDate, 'MM/yyyy') : '...'}`
      : t('financialdashboard.export.all-data');

    const storesLabel = selectedStores.length > 0
      ? selectedStores.map(s => s.label).join('; ')
      : t('financialdashboard.export.all-stores');

    const generatedOn = format(new Date(), 'yyyy-MM-dd');

    // Compute total ex-VAT & VAT from ProductsInPeriod
    let totalExVAT = 0;
    filteredData.forEach(entry => {
      entry.ProductsInPeriod.forEach(p => {
        totalExVAT += p.TotalAmountPaid / (1 + p.VAT / 100);
      });
    });
    const totalVATAmount = kpiData.totalIncome - totalExVAT;

    // Monthly aggregation
    const monthLabels = getTimeLabels(filteredData, monthNames, 'month');
    const monthlyGroups = new Map<string, { revenue: number; cash: number; exVAT: number; transactions: number; items: number }>();
    filteredData.forEach(entry => {
      const key = getGroupKey(entry, 'month');
      const g = monthlyGroups.get(key) || { revenue: 0, cash: 0, exVAT: 0, transactions: 0, items: 0 };
      g.revenue += entry.TotalAmountPaid;
      g.cash += entry.TotalAmountCash ?? 0;
      g.transactions += entry.TransactionCount;
      entry.ProductsInPeriod.forEach(p => {
        g.exVAT += p.TotalAmountPaid / (1 + p.VAT / 100);
        if (p.ProductCode) g.items += p.Units;
      });
      monthlyGroups.set(key, g);
    });

    // Quarterly aggregation
    const quarterLabels = getTimeLabels(filteredData, monthNames, 'quarter');
    const quarterlyGroups = new Map<string, { revenue: number; cash: number; exVAT: number; transactions: number; items: number }>();
    filteredData.forEach(entry => {
      const key = getGroupKey(entry, 'quarter');
      const g = quarterlyGroups.get(key) || { revenue: 0, cash: 0, exVAT: 0, transactions: 0, items: 0 };
      g.revenue += entry.TotalAmountPaid;
      g.cash += entry.TotalAmountCash ?? 0;
      g.transactions += entry.TransactionCount;
      entry.ProductsInPeriod.forEach(p => {
        g.exVAT += p.TotalAmountPaid / (1 + p.VAT / 100);
        if (p.ProductCode) g.items += p.Units;
      });
      quarterlyGroups.set(key, g);
    });

    // Per-item breakdown
    const productPerformances = aggregateProductData(filteredData, products);

    // Build CSV
    const periodHeaders = [
      t('financialdashboard.export.period'),
      t('financialdashboard.export.revenue-incl-vat'),
      t('financialdashboard.export.revenue-ex-vat'),
      t('financialdashboard.export.vat-amount'),
      t('financialdashboard.export.transactions'),
      ...(showCash ? [t('financialdashboard.export.cash-amount')] : []),
      t('financialdashboard.export.items-sold'),
    ];

    const lines: string[] = [
      // Metadata
      row(t('financialdashboard.export.date-range'), rangeLabel),
      row(t('financialdashboard.export.stores'), storesLabel),
      row(t('financialdashboard.export.generated-on'), generatedOn),
      '',
      // Summary
      escapeCSVValue(t('financialdashboard.export.summary-section')),
      row(t('financialdashboard.export.total-revenue-incl-vat'), formatCurrency(kpiData.totalIncome)),
      row(t('financialdashboard.export.total-revenue-ex-vat'), formatCurrency(totalExVAT)),
      row(t('financialdashboard.export.total-vat-amount'), formatCurrency(totalVATAmount)),
      row(t('financialdashboard.export.total-transactions'), kpiData.totalSales),
      row(t('financialdashboard.export.avg-per-month'), formatCurrency(kpiData.avgPerMonth)),
      row(t('financialdashboard.export.avg-per-week'), formatCurrency(kpiData.avgPerWeek)),
      row(t('financialdashboard.export.todays-sales'), kpiData.todaySales),
      row(t('financialdashboard.export.todays-total-amount'), formatCurrency(kpiData.todayAmountPaid)),
      ...(showCash ? [row(t('financialdashboard.export.todays-amount-cash'), formatCurrency(kpiData.todayAmountCash))] : []),
      row(t('financialdashboard.export.avg-items-per-transaction'), kpiData.avgItemsPerTransaction.toFixed(1)),
      '',
      // Monthly
      escapeCSVValue(t('financialdashboard.export.monthly-section')),
      row(...periodHeaders),
      ...monthLabels.map(tl => {
        const g = monthlyGroups.get(tl.key) || { revenue: 0, cash: 0, exVAT: 0, transactions: 0, items: 0 };
        return row(tl.label, formatCurrency(g.revenue), formatCurrency(g.exVAT), formatCurrency(g.revenue - g.exVAT), g.transactions, ...(showCash ? [formatCurrency(g.cash)] : []), g.items);
      }),
      '',
      // Quarterly
      escapeCSVValue(t('financialdashboard.export.quarterly-section')),
      row(...periodHeaders),
      ...quarterLabels.map(tl => {
        const g = quarterlyGroups.get(tl.key) || { revenue: 0, cash: 0, exVAT: 0, transactions: 0, items: 0 };
        return row(tl.label, formatCurrency(g.revenue), formatCurrency(g.exVAT), formatCurrency(g.revenue - g.exVAT), g.transactions, ...(showCash ? [formatCurrency(g.cash)] : []), g.items);
      }),
      '',
      // Per-item
      escapeCSVValue(t('financialdashboard.export.items-section')),
      row(
        t('financialdashboard.export.product-code'),
        t('financialdashboard.export.product-title'),
        t('financialdashboard.export.total-units'),
        t('financialdashboard.export.vat-rate'),
        t('financialdashboard.export.revenue-incl-vat'),
        t('financialdashboard.export.revenue-ex-vat'),
        t('financialdashboard.export.vat-amount'),
        t('financialdashboard.export.avg-units-period'),
        t('financialdashboard.export.avg-revenue-period'),
      ),
      ...productPerformances.map(p =>
        row(
          p.ProductCode,
          p.ProductTitle,
          p.TotalUnits,
          `${p.VAT}%`,
          formatCurrency(p.TotalRevenue),
          formatCurrency(p.TotalRevenueExVAT),
          formatCurrency(p.TotalVATAmount),
          p.AvgUnitsPerPeriod.toFixed(1),
          formatCurrency(p.AvgRevenuePerPeriod),
        )
      ),
    ];

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `financial-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <button
      onClick={handleExport}
      disabled={filteredData.length === 0}
      className="export-products-btn"
    >
      {t('financialdashboard.export.button-label')}
    </button>
  );
};

export default ExportOverviewButton;
